/**
 * Onboarding Application Service
 *
 * Thin client-side facade over `onboardingApi` (HTTP → Fastify → Supabase).
 * Lives in `packages/client` so the hooks in `useOnboarding.ts` can call it
 * without knowing about transport details. All data access goes through the
 * API — the browser never touches Supabase directly.
 *
 * Error handling contract: every method returns `RepositoryResult<T>` so
 * React Query's `queryFn` can surface errors in a structured way without
 * throwing. Axios rejections are caught and mapped to `{ success: false }`.
 */

import type {
    ApplicationEventHandler,
    IMerchant,
    IOnboardingApplication,
    RepositoryResult,
} from '@order-up/models';
import { createLogger } from '../utils/logger';
import { onboardingApi } from './onboardingApi';

const logger = createLogger('OnboardingService');

// ============================================================================
// Service Types
// ============================================================================

export interface CreateApplicationInput {
    /**
     * Firebase UID — kept for API compatibility with the previous
     * implementation, but ignored by the server (the authenticated user
     * from the session cookie is authoritative).
     */
    firebaseUid: string;
    applicationData?: Partial<IMerchant>;
}

export interface UpdateApplicationInput {
    applicationData?: Partial<IMerchant>;
    currentStep?: number;
}

// ============================================================================
// Application Service
// ============================================================================

function toError(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
}

export class OnboardingApplicationService {
    // -------------------------------------------------------------------------
    // Application Lifecycle
    // -------------------------------------------------------------------------

    /**
     * Get (or create) the authenticated user's application.
     * Returns existing application if one exists, otherwise the server
     * creates a new draft. The `input.firebaseUid` argument is accepted for
     * backwards compatibility with the old API surface but is ignored — the
     * server uses the authenticated session uid.
     */
    async createOrGetApplication(
        _input: CreateApplicationInput,
    ): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            logger.debug('Get-or-create application for authenticated user');
            const result = await onboardingApi.getMine();
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('createOrGetApplication failed', { error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /**
     * Get the authenticated user's application.
     * The `firebaseUid` parameter is ignored — the server derives identity
     * from the auth session.
     */
    async getApplicationByUser(
        _firebaseUid: string,
    ): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            const result = await onboardingApi.getMine();
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('getApplicationByUser failed', { error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /**
     * Get an application by ID. Currently proxies through `getMine()` because
     * the server enforces "one application per user" — the caller's own
     * application is always the only one they can access. The `id` parameter
     * is preserved for API compatibility.
     */
    async getApplication(_id: string): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            const result = await onboardingApi.getMine();
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('getApplication failed', { error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /**
     * Check if an application exists for the user.
     * Because `getMine()` is get-or-create, this always returns `true` once
     * the server is reachable. Kept on the interface for compatibility.
     */
    async hasApplication(_firebaseUid: string): Promise<boolean> {
        try {
            const result = await onboardingApi.getMine();
            return Boolean(result.data);
        } catch {
            return false;
        }
    }

    // -------------------------------------------------------------------------
    // Application Updates
    // -------------------------------------------------------------------------

    /** Save draft data for the given application (auto-save during form fill). */
    async saveDraft(
        id: string,
        applicationData: Partial<IMerchant>,
        currentStep: number,
    ): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            logger.debug('Saving draft', { id, currentStep });
            const result = await onboardingApi.update(id, { applicationData, currentStep });
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('saveDraft failed', { id, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /** Update application data. The server merges partial patches with existing data. */
    async updateApplication(
        id: string,
        input: UpdateApplicationInput,
    ): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            const result = await onboardingApi.update(id, {
                applicationData: input.applicationData,
                currentStep: input.currentStep,
            });
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('updateApplication failed', { id, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /** Move to the next step (server-side atomic increment). */
    async nextStep(id: string): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            const result = await onboardingApi.nextStep(id);
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('nextStep failed', { id, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /** Move to the previous step (clamped at 0). */
    async previousStep(id: string): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            const result = await onboardingApi.previousStep(id);
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('previousStep failed', { id, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    /** Jump to a specific step. */
    async goToStep(
        id: string,
        step: number,
    ): Promise<RepositoryResult<IOnboardingApplication>> {
        if (step < 0) {
            return { success: false, error: 'Invalid step' };
        }
        try {
            const result = await onboardingApi.goToStep(id, step);
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('goToStep failed', { id, step, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    // -------------------------------------------------------------------------
    // Submission
    // -------------------------------------------------------------------------

    /**
     * Validate (server-side) and submit the application for underwriting.
     */
    async submitApplication(id: string): Promise<RepositoryResult<IOnboardingApplication>> {
        try {
            logger.debug('Submitting application', { id });
            const result = await onboardingApi.submit(id);
            return { success: true, data: result.data };
        } catch (err) {
            logger.error('submitApplication failed', { id, error: toError(err) });
            return { success: false, error: toError(err) };
        }
    }

    // -------------------------------------------------------------------------
    // Subscriptions (no-ops — polling replaces Supabase realtime)
    // -------------------------------------------------------------------------
    //
    // The old implementation opened Supabase postgres_changes WebSockets.
    // Now that we route through the API, the onboarding hooks poll via
    // React Query's `refetchInterval` while status is non-terminal.
    // These methods remain on the service surface so the existing hooks
    // (`useApplicationSubscription`, `useUserApplicationSubscription`) can
    // still call them without changes — they just return a no-op unsubscribe.

    subscribeToApplication(_id: string, _handler: ApplicationEventHandler): () => void {
        return () => {};
    }

    subscribeToUserApplications(
        _firebaseUid: string,
        _handler: ApplicationEventHandler,
    ): () => void {
        return () => {};
    }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let serviceInstance: OnboardingApplicationService | null = null;

/** Get the OnboardingApplicationService singleton. */
export function getOnboardingService(): OnboardingApplicationService {
    if (!serviceInstance) {
        serviceInstance = new OnboardingApplicationService();
    }
    return serviceInstance;
}

/** Reset the service instance (for testing). */
export function resetOnboardingService(): void {
    serviceInstance = null;
}
